// AGC: Price History Service
// AGC: Manages price history for inputs (insumos)
// AGC: Stores price entries with timestamps for cost analysis

import { getBackendUrl } from '../utils/backendUrl'

const BASE_URL = getBackendUrl()

// =============================================================================
// AGC: TYPES
// =============================================================================

export type PriceSource = 'manual' | 'invoice' | 'admin'

export interface PriceEntry {
  id: string
  inputId: string
  inputName: string
  pricePerUnit: number
  currency: string
  unit: string
  source: PriceSource
  recordedAt: Date | string
  quantity?: number       // AGC: Quantity from stock entry (for reference)
  totalPrice?: number     // AGC: Total price from stock entry
  notes?: string
}

export interface PriceHistoryFilters {
  inputId?: string
  startDate?: Date
  endDate?: Date
  source?: PriceSource
}

export interface PriceSummary {
  inputId: string
  inputName: string
  unit: string
  latestPrice: number
  averagePrice: number
  minPrice: number
  maxPrice: number
  entryCount: number
  lastUpdated: Date | string
}

// =============================================================================
// AGC: API FUNCTIONS
// =============================================================================

/**
 * AGC: Record a new price entry
 * AGC: Called after stock entry with price information
 */
export const recordPriceEntry = async (
  entry: Omit<PriceEntry, 'id' | 'recordedAt'>,
  authToken: string
): Promise<PriceEntry> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/prices/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(entry),
    })

    if (!response.ok) {
      throw new Error(`Failed to record price entry: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data.priceEntry
  } catch (error) {
    console.error('AGC: Error recording price entry:', error)
    throw error
  }
}

/**
 * AGC: Get price history for an input
 */
export const getPriceHistory = async (
  inputId: string,
  authToken: string,
  filters?: PriceHistoryFilters
): Promise<PriceEntry[]> => {
  try {
    const params = new URLSearchParams()
    if (filters?.startDate) params.append('startDate', filters.startDate.toISOString())
    if (filters?.endDate) params.append('endDate', filters.endDate.toISOString())
    if (filters?.source) params.append('source', filters.source)

    const url = `${BASE_URL}/api/v1/prices/history/${inputId}${params.toString() ? `?${params.toString()}` : ''}`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        return [] // AGC: No history found is not an error
      }
      throw new Error(`Failed to get price history: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data.history || []
  } catch (error) {
    console.error('AGC: Error getting price history:', error)
    return []
  }
}

/**
 * AGC: Get latest price for an input
 */
export const getLatestPrice = async (
  inputId: string,
  authToken: string
): Promise<PriceEntry | null> => {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/prices/latest/${inputId}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null // AGC: No price found
      }
      throw new Error(`Failed to get latest price: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data.price || null
  } catch (error) {
    console.error('AGC: Error getting latest price:', error)
    return null
  }
}

/**
 * AGC: Get price summary for all inputs or specific ones
 */
export const getPriceSummaries = async (
  authToken: string,
  inputIds?: string[]
): Promise<PriceSummary[]> => {
  try {
    const params = new URLSearchParams()
    if (inputIds && inputIds.length > 0) {
      params.append('inputIds', inputIds.join(','))
    }

    const url = `${BASE_URL}/api/v1/prices/summary${params.toString() ? `?${params.toString()}` : ''}`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to get price summaries: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data || data.summaries || []
  } catch (error) {
    console.error('AGC: Error getting price summaries:', error)
    return []
  }
}

/**
 * AGC: Get admin catalog prices (fallback prices from master catalog)
 */
export const getAdminCatalogPrices = async (
  authToken: string,
  inputIds?: string[]
): Promise<Record<string, number>> => {
  try {
    const params = new URLSearchParams()
    if (inputIds && inputIds.length > 0) {
      params.append('inputIds', inputIds.join(','))
    }

    const url = `${BASE_URL}/api/v1/prices/catalog${params.toString() ? `?${params.toString()}` : ''}`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    })

    if (!response.ok) {
      // AGC: Catalog prices are optional, return empty on error
      console.warn('AGC: Could not fetch catalog prices')
      return {}
    }

    const data = await response.json()
    return data.data || data.prices || {}
  } catch (error) {
    console.error('AGC: Error getting catalog prices:', error)
    return {}
  }
}

// =============================================================================
// AGC: UTILITY FUNCTIONS
// =============================================================================

/**
 * AGC: Calculate price statistics from history
 */
export const calculatePriceStats = (history: PriceEntry[]): {
  average: number
  min: number
  max: number
  trend: 'up' | 'down' | 'stable'
} => {
  if (history.length === 0) {
    return { average: 0, min: 0, max: 0, trend: 'stable' }
  }

  const prices = history.map(h => h.pricePerUnit)
  const average = prices.reduce((sum, p) => sum + p, 0) / prices.length
  const min = Math.min(...prices)
  const max = Math.max(...prices)

  // AGC: Calculate trend based on recent vs older prices
  let trend: 'up' | 'down' | 'stable' = 'stable'
  if (history.length >= 2) {
    const sortedByDate = [...history].sort((a, b) => 
      new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
    )
    const recent = sortedByDate.slice(0, Math.ceil(history.length / 2))
    const older = sortedByDate.slice(Math.ceil(history.length / 2))
    
    const recentAvg = recent.reduce((sum, h) => sum + h.pricePerUnit, 0) / recent.length
    const olderAvg = older.length > 0 
      ? older.reduce((sum, h) => sum + h.pricePerUnit, 0) / older.length 
      : recentAvg

    const changePercent = ((recentAvg - olderAvg) / olderAvg) * 100
    if (changePercent > 5) trend = 'up'
    else if (changePercent < -5) trend = 'down'
  }

  return { average, min, max, trend }
}

/**
 * AGC: Format price for display
 */
export const formatPrice = (
  price: number,
  currency: string = 'BRL',
  unit?: string
): string => {
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price)

  return unit ? `${formatted}/${unit}` : formatted
}

/**
 * AGC: Get price with fallback logic
 * AGC: Priority: User price history > Admin catalog > null
 */
export const getPriceWithFallback = async (
  inputId: string,
  authToken: string
): Promise<{ price: number | null; source: PriceSource | null }> => {
  // AGC: Try user's price history first
  const userPrice = await getLatestPrice(inputId, authToken)
  if (userPrice) {
    return { price: userPrice.pricePerUnit, source: userPrice.source }
  }

  // AGC: Fallback to admin catalog
  const catalogPrices = await getAdminCatalogPrices(authToken, [inputId])
  if (catalogPrices[inputId]) {
    return { price: catalogPrices[inputId], source: 'admin' }
  }

  return { price: null, source: null }
}

export default {
  recordPriceEntry,
  getPriceHistory,
  getLatestPrice,
  getPriceSummaries,
  getAdminCatalogPrices,
  calculatePriceStats,
  formatPrice,
  getPriceWithFallback,
}

